import { useState } from 'react'
import { Compass, RotateCcw, Undo2, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { GEOREF_URL } from '@core/links'
import {
  IDENTITY_PLACEMENT,
  placementsDiffer,
  type GeoPlacement,
} from '@viewer/lib/geoTransform'
import { zipSync } from 'fflate'
import { rawIfc } from '@viewer/lib/rawIfc'
import { datumDoCampo, gravarGeorefNoIfc, type GravacaoDaGeoref } from '@viewer/lib/georef/gravarGeoref'
import { normalizeEpsg } from '@viewer/map/crsDefs'
import { downloadBlob } from '@core/lib/downloadBlob'

// Per-model placement editor surfaced inside the sidebar model row.
//
// Onda B da federação geo: o utilizador pode introduzir manualmente
// Eastings / Northings / Altura / Heading / Escala para modelos cujo
// IFC não traz IfcMapConversion (típico em exports Revit sem survey
// point). Para modelos com MapConversion o painel mostra os valores
// auto-derivados, e qualquer alteração transforma-os em override.
//
// O override substitui completamente o baseline (não é delta). O
// botão "Reset" remove o override e volta a aplicar o baseline.
//
// Valores estão na convenção IFC (Eastings = X-east, Northings =
// Y-north, OrthogonalHeight = Z-up, Heading = ângulo CCW do eixo X
// local em relação ao east da map CRS). A conversão para o frame
// Three é feita em viewer/lib/geoTransform.ts.

interface Props {
  modelId: string
  /** Closes the editor; called when user clicks the X or "Aplicar". */
  onClose: () => void
}

function GeoPlacementEditor({ modelId, onClose }: Props) {
  const baseline = useAppStore(
    (s) => s.geoBaselines[modelId] ?? IDENTITY_PLACEMENT,
  )
  const override = useAppStore((s) => s.geoOverrides[modelId] ?? null)
  const canUndo = useAppStore((s) => modelId in s.geoUndo)
  const setGeoOverride = useAppStore((s) => s.setGeoOverride)
  const clearGeoOverride = useAppStore((s) => s.clearGeoOverride)
  const undoGeoOverride = useAppStore((s) => s.undoGeoOverride)
  const pushToast = useAppStore((s) => s.pushToast)
  const crsMeta = useAppStore((s) => s.crsMeta[modelId] ?? null)
  const crsOverride = useAppStore((s) => s.crsOverrides[modelId] ?? null)
  const detectedBaked = useAppStore(
    (s) => s.geoDetectedBaked[modelId] ?? null,
  )
  const setCrsOverride = useAppStore((s) => s.setCrsOverride)
  const clearCrsOverride = useAppStore((s) => s.clearCrsOverride)
  const [crsDraft, setCrsDraft] = useState<string | null>(null)

  const effectiveCrs = crsOverride ?? crsMeta
  const commitCrs = (raw: string) => {
    const v = raw.trim()
    setCrsDraft(null)
    // Empty input = back to the value read from the IFC (clear override).
    if (v === '' || v === (crsMeta ?? '')) {
      clearCrsOverride(modelId)
      return
    }
    // Accept "EPSG:3763", "epsg:3763" or bare "3763" (normalised).
    const norm = /^\d+$/.test(v) ? `EPSG:${v}` : v.toUpperCase().startsWith('EPSG:') ? `EPSG:${v.slice(5).trim()}` : v
    setCrsOverride(modelId, norm)
  }

  const active = override ?? baseline
  const hasOverride = override !== null && placementsDiffer(override, baseline)
  const baselineIsIdentity = !placementsDiffer(baseline, IDENTITY_PLACEMENT)

  // Local draft per field; only commit to the store on blur or
  // Enter, so the user can type "-12345.678" without each
  // intermediate stroke firing a transform refresh.
  const [draft, setDraft] = useState<Partial<Record<keyof GeoPlacement, string>>>({})

  const fields: Array<{
    key: keyof GeoPlacement
    label: string
    suffix: string
    step: number
  }> = [
    { key: 'eastings', label: 'Eastings (E)', suffix: 'm', step: 0.001 },
    { key: 'northings', label: 'Northings (N)', suffix: 'm', step: 0.001 },
    {
      key: 'orthogonalHeight',
      label: 'Altura ortogonal (h)',
      suffix: 'm',
      step: 0.001,
    },
    { key: 'rotationDeg', label: 'Heading', suffix: '°', step: 0.01 },
    { key: 'scale', label: 'Escala', suffix: '×', step: 0.0001 },
  ]

  const commit = (key: keyof GeoPlacement, raw: string) => {
    const trimmed = raw.trim().replace(',', '.')
    if (trimmed === '') {
      setDraft((d) => {
        const next = { ...d }
        delete next[key]
        return next
      })
      return
    }
    const n = Number(trimmed)
    if (!Number.isFinite(n)) {
      // Reject: revert the draft.
      setDraft((d) => {
        const next = { ...d }
        delete next[key]
        return next
      })
      return
    }
    if (n === active[key]) return
    setGeoOverride(modelId, { [key]: n } as Partial<GeoPlacement>)
    setDraft((d) => {
      const next = { ...d }
      delete next[key]
      return next
    })
  }

  return (
    <div className="border-t border-[var(--color-border-subtle)] bg-[var(--color-bg)]/40 px-2 py-2">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          <Compass size={11} />
          Posição
          {hasOverride ? (
            <span className="rounded border border-amber-700/60 bg-[var(--color-warn-bg)] px-1 text-[9px] text-[var(--color-warn-text)]">
              manual
            </span>
          ) : baselineIsIdentity ? (
            <span className="rounded border border-[var(--color-border)] px-1 text-[9px] text-[var(--color-text-dim)]">
              origem
            </span>
          ) : (
            <span className="rounded border border-emerald-700/60 bg-[var(--color-ok-bg)] px-1 text-[9px] text-[var(--color-ok-text)]">
              auto · MapConversion
            </span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {canUndo && (
            <button
              type="button"
              onClick={() => {
                undoGeoOverride(modelId)
                pushToast('Posição desfeita', { type: 'info' })
              }}
              title="Desfazer a última alteração de posição (um passo). O Reset volta ao MapConversion/origem do modelo."
              className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              <Undo2 size={10} />
              Desfazer
            </button>
          )}
          {hasOverride && (
            <button
              type="button"
              onClick={() => clearGeoOverride(modelId)}
              title="Reverter para o IfcMapConversion (ou origem) do modelo"
              className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              <RotateCcw size={10} />
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            title="Fechar editor de posição"
            aria-label="Fechar"
            className="flex h-4 w-4 items-center justify-center rounded text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            <X size={11} />
          </button>
        </div>
      </div>
      {detectedBaked && !hasOverride && baselineIsIdentity && (
        <div className="mb-1.5 rounded border border-amber-700/50 bg-[var(--color-warn-bg)] px-2 py-1.5 text-[10px] text-[var(--color-warn-text)]">
          <div className="font-medium">
            Coordenadas embutidas detetadas (sem IfcMapConversion)
          </div>
          <div className="mt-0.5 font-mono text-[10px] text-[var(--color-warn-text)]/90">
            E={detectedBaked.eastings.toFixed(2)} · N=
            {detectedBaked.northings.toFixed(2)} · h=
            {detectedBaked.orthogonalHeight.toFixed(2)}
          </div>
          <div className="mt-0.5 text-[var(--color-warn-text)]/70">
            Este ficheiro traz as coordenadas do terreno embutidas nos
            placements (padrão pré-IFC4). O viewer já posiciona a geometria
            nas coordenadas verdadeiras do ficheiro; define o CRS (ex.:
            EPSG:3763) para o mapa saber onde é.
          </div>
        </div>
      )}
      <div className="mb-1.5 grid grid-cols-[auto_1fr_auto] items-center gap-x-2 text-[11px]">
        <span
          className="text-[var(--color-text-muted)]"
          title="Sistema de coordenadas projectado (IfcProjectedCRS.Name). Lido do IFC quando declarado; podes sobrescrever aqui (ex.: EPSG:3763). Este valor identifica o referencial dos Eastings/Northings e é usado pelo mapa; não escreve no ficheiro IFC. Para gravar o CRS no próprio IFC usa o ifc-georeferencer (link em baixo)."
        >
          CRS
        </span>
        <input
          type="text"
          value={crsDraft ?? effectiveCrs ?? ''}
          placeholder="ex.: EPSG:3763"
          onChange={(e) => setCrsDraft(e.target.value)}
          onBlur={(e) => commitCrs(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitCrs((e.target as HTMLInputElement).value)
            } else if (e.key === 'Escape') {
              setCrsDraft(null)
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          className="min-w-0 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none"
        />
        <span
          className={`rounded border px-1 text-[9px] ${
            crsOverride
              ? 'border-amber-700/60 bg-[var(--color-warn-bg)] text-[var(--color-warn-text)]'
              : crsMeta
                ? 'border-emerald-700/60 bg-[var(--color-ok-bg)] text-[var(--color-ok-text)]'
                : 'border-[var(--color-border)] text-[var(--color-text-dim)]'
          }`}
          title={
            crsOverride
              ? 'CRS definido manualmente nesta sessão (apaga o campo para voltar ao do IFC)'
              : crsMeta
                ? 'CRS lido do IfcProjectedCRS do ficheiro'
                : 'O IFC não declara IfcProjectedCRS; define um código EPSG se souberes o referencial'
          }
        >
          {crsOverride ? 'manual' : crsMeta ? 'auto' : 'n/d'}
        </span>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-1 text-[11px]">
        {fields.map((f) => {
          const current = active[f.key]
          const value =
            draft[f.key] !== undefined ? draft[f.key]! : String(current)
          return (
            <div key={f.key} className="contents">
              <span className="text-[var(--color-text-muted)]">{f.label}</span>
              <input
                type="text"
                inputMode="decimal"
                value={value}
                step={f.step}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                }
                onBlur={(e) => commit(f.key, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commit(f.key, (e.target as HTMLInputElement).value)
                  } else if (e.key === 'Escape') {
                    setDraft((d) => {
                      const next = { ...d }
                      delete next[f.key]
                      return next
                    })
                    ;(e.target as HTMLInputElement).blur()
                  }
                }}
                className="min-w-0 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none"
              />
              <span className="text-[10px] text-[var(--color-text-dim)]">
                {f.suffix}
              </span>
            </div>
          )
        })}
      </div>
      <p className="mt-1.5 text-[10px] leading-tight text-[var(--color-text-dim)]">
        Valores absolutos na map CRS. Heading = ângulo CCW do eixo X
        local em relação ao East. Enter / blur grava; Esc cancela.
      </p>
      <SaveGeorefToFileBlock
        modelId={modelId}
        placement={active}
        epsg={effectiveCrs}
      />
      <p className="mt-1 text-[10px] leading-tight text-[var(--color-text-dim)]">
        Em alternativa,{' '}
        <a
          href={GEOREF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-accent)] hover:underline"
          title="ifc-georeferencer (instância VN2R da ferramenta open source do buildingSMART NL): coloca o IFC num mapa e escreve o IfcMapConversion + IfcProjectedCRS de volta no ficheiro, tudo no browser. Depois recarrega o ficheiro aqui."
        >
          georreferencia com o ifc-georeferencer ↗
        </a>{' '}
        (útil para IFC2X3 e para posicionar arrastando num mapa grande).
      </p>
    </div>
  )
}
// Botão "Gravar georref no IFC": escreve o placement efetivo + CRS como
// IfcProjectedCRS/IfcMapConversion (ePSets no IFC2X3) nos bytes
// originais e descarrega <nome>_georef.ifc; com o datum vertical e a
// latitude/longitude do IfcSite. "Gravar todos" faz o mesmo a cada
// modelo aberto (cada um com o seu placement e o seu CRS, ou o deste
// quando não tem) e descarrega um .zip. O original nunca é tocado.
function SaveGeorefToFileBlock({
  modelId,
  placement,
  epsg,
}: {
  modelId: string
  placement: GeoPlacement
  epsg: string | null
}) {
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null,
  )
  const [datum, setDatum] = useState('')
  const [corrigirSitio, setCorrigirSitio] = useState(true)
  const [tirarGrandes, setTirarGrandes] = useState(true)
  const [ocupado, setOcupado] = useState(false)
  const models = useAppStore((s) => s.models)
  const fileName = models.find((m) => m.id === modelId)?.fileName ?? modelId
  const epsgOk = !!epsg && /^EPSG:\d+$/.test(normalizeEpsg(epsg))
  const nomeGeoref = (f: string) => `${f.replace(/\.ifc$/i, '')}_georef.ifc`

  const resumo = (r: GravacaoDaGeoref) =>
    `${r.removidas} entidade${r.removidas === 1 ? '' : 's'} de georref antiga${r.removidas === 1 ? '' : 's'} substituída${r.removidas === 1 ? '' : 's'}` +
    (r.sitio
      ? `; IfcSite em ${r.sitio.lat.toFixed(6)}, ${r.sitio.lon.toFixed(6)}`
      : r.avisoDoSitio
        ? `; ${r.avisoDoSitio}`
        : '') +
    (r.recentragem
      ? `; geometria trazida para junto da origem (${(r.recentragem.metros / 1000).toFixed(1).replace('.', ',')} km passados para a IfcMapConversion)`
      : r.avisoDaRecentragem
        ? `; ${r.avisoDaRecentragem}`
        : '')

  const onSave = async () => {
    setMsg(null)
    const bytes = rawIfc.getCachedBytes(modelId)
    if (!bytes) {
      setMsg({
        kind: 'err',
        text: 'Bytes do IFC não estão em cache; recarrega o modelo.',
      })
      return
    }
    setOcupado(true)
    try {
      const res = await gravarGeorefNoIfc(bytes, {
        epsg: epsg!,
        placement,
        verticalDatum: datumDoCampo(datum),
        corrigirSitio,
        tirarCoordenadasGrandes: tirarGrandes,
      })
      const outName = nomeGeoref(fileName)
      downloadBlob(res.bytes.slice().buffer, outName)
      useAppStore.getState().pushToast(`Georreferenciação gravada em ${outName}`, {
        type: 'success',
      })
      setMsg({ kind: 'ok', text: `Ficheiro _georef.ifc descarregado (${resumo(res)}).` })
    } catch (err) {
      setMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Falha a escrever o IFC.',
      })
    } finally {
      setOcupado(false)
    }
  }

  const onSaveAll = async () => {
    setMsg(null)
    setOcupado(true)
    try {
      const st = useAppStore.getState()
      const entradas: Record<string, Uint8Array> = {}
      const notas: string[] = []
      for (const m of st.models) {
        const b = rawIfc.getCachedBytes(m.id)
        if (!b) {
          notas.push(`${m.fileName}: sem os bytes em cache (recarregue-o)`)
          continue
        }
        const crs = st.crsOverrides[m.id] ?? st.crsMeta[m.id] ?? epsg
        if (!crs || !/^EPSG:\d+$/.test(normalizeEpsg(crs))) {
          notas.push(`${m.fileName}: sem CRS EPSG`)
          continue
        }
        try {
          const r = await gravarGeorefNoIfc(b, {
            epsg: normalizeEpsg(crs),
            placement: st.geoOverrides[m.id] ?? st.geoBaselines[m.id] ?? IDENTITY_PLACEMENT,
            verticalDatum: datumDoCampo(datum),
            corrigirSitio,
            tirarCoordenadasGrandes: tirarGrandes,
          })
          entradas[nomeGeoref(m.fileName)] = r.bytes
          if (r.avisoDoSitio) notas.push(`${m.fileName}: ${r.avisoDoSitio}`)
          if (r.avisoDaRecentragem) notas.push(`${m.fileName}: ${r.avisoDaRecentragem}`)
        } catch (err) {
          notas.push(`${m.fileName}: ${err instanceof Error ? err.message : 'falhou'}`)
        }
      }
      const n = Object.keys(entradas).length
      if (n === 0) {
        setMsg({ kind: 'err', text: `Nenhum modelo gravado. ${notas.join('; ')}` })
        return
      }
      const zipado = zipSync(entradas, { level: 6 })
      downloadBlob(zipado.slice().buffer, 'modelos_georef.zip', 'application/zip')
      setMsg({
        kind: notas.length ? 'err' : 'ok',
        text:
          `${n} modelo${n === 1 ? '' : 's'} gravado${n === 1 ? '' : 's'} em modelos_georef.zip` +
          (notas.length ? `. Ficaram de fora ou com aviso: ${notas.join('; ')}` : '.'),
      })
    } finally {
      setOcupado(false)
    }
  }

  return (
    <div className="mt-1.5 flex flex-col gap-1 text-[10px]">
      <div className="flex flex-wrap items-center gap-2">
        <label
          className="flex items-center gap-1 text-[var(--color-text-muted)]"
          title="A referência das altitudes (cotas do IFC). Em Portugal continental, Cascais: EPSG:5780. Vazio: mantém o que o ficheiro já tiver."
        >
          Datum vertical
          <input
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            placeholder="ex.: EPSG:5780"
            list="vn2r-datums-verticais"
            className="w-24 rounded border border-[var(--color-border)] bg-transparent px-1 py-0.5 text-[var(--color-text)]"
            aria-label="Datum vertical (EPSG)"
          />
          <datalist id="vn2r-datums-verticais">
            <option value="EPSG:5780">Cascais (Portugal continental)</option>
            <option value="EPSG:5782">Alicante (Espanha)</option>
            <option value="EPSG:5773">EGM96</option>
            <option value="EPSG:3855">EGM2008</option>
          </datalist>
        </label>
        <label
          className="flex items-center gap-1 text-[var(--color-text-muted)]"
          title="Escreve no IfcSite a latitude/longitude da origem do sítio no mapa (o Revit deixa muitas vezes a localização por omissão, noutro país)."
        >
          <input type="checkbox" checked={corrigirSitio} onChange={(e) => setCorrigirSitio(e.target.checked)} />
          Corrigir latitude/longitude do IfcSite
        </label>
        <label
          className="flex items-center gap-1 text-[var(--color-text-muted)]"
          title="Traz a geometria para junto da origem do ficheiro: a colocação do IfcSite passa para a origem e a deslocação (as coordenadas de mapa que o Revit lá põe) soma-se aos Eastings/Northings da IfcMapConversion. Cada elemento fica no mesmo sítio do mapa. Só se faz quando a deslocação está toda na colocação do IfcSite; caso contrário, o ficheiro grava-se sem esta alteração e é indicado porquê."
        >
          <input type="checkbox" checked={tirarGrandes} onChange={(e) => setTirarGrandes(e.target.checked)} />
          Tirar as coordenadas grandes
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={!epsgOk || ocupado}
          className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 font-medium text-[var(--color-accent)] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          title={
            epsgOk
              ? 'Escreve a georreferenciação com estes valores nos bytes do IFC e descarrega um <nome>_georef.ifc. O original não é alterado. IFC4+: IfcProjectedCRS + IfcMapConversion nativos; IFC2X3: property sets ePSet_MapConversion / ePSet_ProjectedCRS no IfcProject (backport do guia de georreferenciação do buildingSMART).'
              : 'Define primeiro um CRS EPSG numérico (ex.: EPSG:3763) no campo CRS.'
          }
        >
          Gravar georref no IFC (download)
        </button>
        {models.length > 1 && (
          <button
            type="button"
            onClick={() => void onSaveAll()}
            disabled={!epsgOk || ocupado}
            className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:cursor-not-allowed disabled:opacity-40"
            title="Grava a georreferenciação em todos os modelos abertos (cada um com a sua posição e o seu CRS; o deste para os que não têm) e descarrega um .zip com os _georef.ifc."
          >
            Gravar todos ({models.length}, .zip)
          </button>
        )}
      </div>
      {msg && (
        <div
          className={
            'leading-tight ' +
            (msg.kind === 'ok' ? 'text-[var(--color-ok-text)]' : 'text-[var(--color-error-text)]')
          }
        >
          {msg.text}
        </div>
      )}
    </div>
  )
}

export default GeoPlacementEditor
